// Pure helpers for the Visual Editor's "Edit text & images" mode.
// Contract: docs/inline-edit-lab.md §3 (Measure), §4 (Change), §8 (panel).
// No React, no DOM: everything here is unit-tested in inlineEdit.test.js.

export const PHONE_WIDTH = 390;
// The hidden frame that checks every change at phone size (390 × 860).
export const PHONE_CHECK_HEIGHT = 860;
export const WIDTHS = ['desktop', 'phone'];
export const MAX_CHANGES = 30;
export const MAX_IMAGE_BYTES = 10 * 1024 * 1024;
export const IMAGE_TYPES = ['image/jpeg', 'image/png', 'image/webp'];
export const ALT_MAX = 250;

const HEADING = /^h[1-6]$/;

// Card label for the element a change targets.
export function targetLabel(tag) {
  const t = String(tag || '').toLowerCase();
  if (HEADING.test(t)) return 'Heading';
  if (t === 'p') return 'Paragraph';
  if (t === 'li') return 'List item';
  if (t === 'a') return 'Link';
  if (t === 'button') return 'Button';
  if (t === 'img') return 'Image';
  return 'Text';
}

export function isHeadingTag(tag) {
  return HEADING.test(String(tag || '').toLowerCase());
}

export function pluralize(n, one, many = `${one}s`) {
  return `${n} ${n === 1 ? one : many}`;
}

// One verdict across both widths:
//   'blocked'  if any measured width is blocked,
//   'checking' if a width has not been measured yet,
//   'grew'     if any width grew,
//   'fits'     otherwise.
export function aggregateVerdict(measures) {
  const m = measures || {};
  const got = WIDTHS.map((w) => m[w]).filter(Boolean);
  if (got.some((x) => x.verdict === 'blocked')) return 'blocked';
  if (got.length < WIDTHS.length) return 'checking';
  if (got.some((x) => x.verdict === 'grew')) return 'grew';
  return 'fits';
}

// Details for a card: which widths are blocked, their reasons, and how many
// lines the text wraps onto now (the larger of the two widths that grew).
export function verdictDetails(measures) {
  const m = measures || {};
  const verdict = aggregateVerdict(m);
  const blockedWidths = WIDTHS.filter((w) => m[w]?.verdict === 'blocked');
  const reasons = [...new Set(blockedWidths.flatMap((w) => m[w]?.reasons || []))];
  const grewLines = Math.max(0, ...WIDTHS.filter((w) => m[w]?.verdict === 'grew').map((w) => m[w].linesAfter || 0));
  const missingWidths = WIDTHS.filter((w) => !m[w]);
  return { verdict, blockedWidths, reasons, grewLines, missingWidths };
}

const REASON_TEXT = {
  clipped: 'Part of it gets cut off by the box it sits in.',
  spill: 'It spills out of its section.',
  doc_overflow_x: 'It makes the page scroll sideways.',
  long_token: 'It has a very long word or web address with no spaces, which can’t wrap.',
  empty: 'It can’t be left empty.',
  heading_too_long: 'The heading now runs onto too many lines.',
};

// Measure reasons → plain-English sentences (unknown reasons are dropped).
export function describeReasons(reasons) {
  return [...new Set(reasons || [])].map((r) => REASON_TEXT[r]).filter(Boolean);
}

// Notes that never block a publish (measure.reasons on a width that fits).
const SOFT_TEXT = {
  generic_label: 'This link text is vague — say where it leads.',
};
export const SOFT_REASONS = Object.keys(SOFT_TEXT);

// Soft warnings across both widths → plain sentences (deduplicated).
export function softWarnings(measures) {
  const m = measures || {};
  const reasons = new Set(WIDTHS.flatMap((w) => m[w]?.reasons || []));
  return SOFT_REASONS.filter((r) => reasons.has(r)).map((r) => SOFT_TEXT[r]);
}

// What to do about a blocked card.
export function fixAdvice(change) {
  if (change?.kind === 'image') return 'Undo and pick a smaller picture.';
  const tag = change?.fingerprint?.tag;
  if (isHeadingTag(tag)) return 'Shorten this heading.';
  if (tag === 'a' || tag === 'button') return `Shorten this ${targetLabel(tag).toLowerCase()}.`;
  return 'Shorten this text.';
}

const BLOCKED_TEXT = {
  external_image: 'This picture comes from another website, so it can’t be swapped here.',
  svg_image: 'This is a drawn graphic rather than a photo, so it can’t be swapped here.',
  picture_source: 'This picture changes with screen size, so it can’t be swapped here.',
  too_small: 'This picture is too small to swap here.',
  locked: 'This part of the page is locked for editing.',
  markup_changed: 'That edit changed a link or formatting inside the text, so we put it back. Change only the words.',
  empty: 'Headings, links and buttons need words — not left empty or only punctuation — so we put the text back.',
  too_long: 'That text is too long. Keep headings under 200 characters and other text under 1000.',
  mixed_content: 'This text is mixed with other page parts, so it can’t be edited here.',
  form: 'Forms can’t be edited here.',
  not_editable: 'There’s no editable text or picture there.',
  named_control: 'This button has a screen-reader label set by the designer, so its text can’t be changed here. Ask us and we’ll update it.',
  hidden_text: 'That text is only for screen readers and can’t be edited here.',
  preview_failed: 'That picture couldn’t be shown in the page. Try a JPEG, PNG or WebP picture.',
  action_failed: 'Something went wrong in the page. Try that again.',
};

// NEXSAV_INLINE_BLOCKED reason → sentence for the notice in the panel.
export function describeBlocked(reason) {
  return BLOCKED_TEXT[reason] || 'That part of the page can’t be edited here.';
}

function baseName(src) {
  const s = String(src || '').split(/[?#]/)[0];
  return s.split('/').filter(Boolean).pop() || '';
}
export { baseName as imageFileName };

// The Change as the API expects it (§4): client-only fields dropped, image
// changes carrying the upload's attachment key, and both widths' measures.
//   entries: [{ change, measures }] (or bare Change objects)
//   uploads: Map | object changeId → { key, filename, size, contentType, alt?, decorative?, nameBearing? }
// A decorative picture (the bridge's target.decorative) is sent with alt ''
// and `decorative: true`; a picture that is the only label of a link or
// button carries `nameBearing: true` (the API then insists on a real name).
export function buildCreatePayload(entries, uploads) {
  const get = (id) => (uploads instanceof Map ? uploads.get(id) : uploads?.[id]);
  const attachments = [];
  const changes = (entries || []).map((entry) => {
    const change = entry?.change || entry;
    const measures = entry?.change ? entry.measures : change.measure;
    const out = {
      id: change.id,
      kind: change.kind,
      path: change.path,
      selector: change.selector,
      fingerprint: change.fingerprint,
      before: change.before,
      after: change.after,
    };
    if (change.kind === 'image') {
      const up = get(change.id);
      const decorative = !!(up?.decorative ?? change.decorative);
      const nameBearing = !!(up?.nameBearing ?? change.nameBearing);
      if (up) {
        out.after = {
          attachmentKey: up.key,
          filename: up.filename,
          contentType: up.contentType,
          alt: decorative ? '' : (up.alt ?? change.after?.alt ?? ''),
        };
        if (!attachments.some((a) => a.key === up.key)) {
          attachments.push({ key: up.key, filename: up.filename, size: up.size || 0, contentType: up.contentType });
        }
      }
      if (decorative) out.decorative = true;
      if (nameBearing) out.nameBearing = true;
    }
    const measure = {};
    if (measures?.desktop) measure.desktop = measures.desktop;
    if (measures?.phone) measure.phone = measures.phone;
    if (Object.keys(measure).length) out.measure = measure;
    return out;
  });
  return { source: 'inline', changes, attachments };
}

const IMAGE_FILE_NAME = /\.(jpe?g|png|webp|gif|svg|avif|bmp|tiff?|heic)$/i;
export const NAME_ALT_MIN = 3;

// The description a new picture needs, in words (null = fine).
//   upload: { key, alt, decorative, nameBearing }
export function imageDescriptionProblem(upload, fallbackAlt = '') {
  if (upload?.decorative) return null;
  const alt = String(upload?.alt ?? fallbackAlt ?? '').trim();
  if (!alt) return 'Describe the new picture first.';
  if (upload?.nameBearing && (alt.length < NAME_ALT_MIN || IMAGE_FILE_NAME.test(alt))) {
    return 'Describe where the picture’s link or button leads.';
  }
  return null;
}

function uploadOf(uploads, id) {
  return uploads instanceof Map ? uploads.get(id) : uploads?.[id];
}

function imagesProblem(entries, uploads) {
  for (const e of entries) {
    if (e.change?.kind !== 'image') continue;
    const up = uploadOf(uploads, e.change.id);
    if (!up?.key) return 'Choose the new picture again.';
    const problem = imageDescriptionProblem(up, e.change.after?.alt);
    if (problem) return problem;
  }
  return null;
}

// Where one width stands for one change:
//   'fits' | 'blocked' (measured), 'checking' (a measure is on its way),
//   'unchecked' (nobody is measuring it: the person has to look).
function widthStatus(measures, width, checking) {
  const m = measures?.[width];
  if (m) return m.verdict === 'blocked' ? 'blocked' : 'fits';
  return checking ? 'checking' : 'unchecked';
}

// Both widths of one change. The phone width is checked automatically in a
// hidden frame (`phoneAuto` 'starting' | 'on'); when that is 'off' or failed
// for this change, the person checks it with the Phone preview.
//   ctx: { pendingWidth, phoneAuto, phoneCheck }
export function checkStatus(entry, ctx = {}) {
  const m = entry?.measures || {};
  const autoPhone = (ctx.phoneAuto === 'on' || ctx.phoneAuto === 'starting') && ctx.phoneCheck !== 'failed';
  return {
    desktop: widthStatus(m, 'desktop', ctx.pendingWidth === 'desktop'),
    phone: widthStatus(m, 'phone', ctx.pendingWidth === 'phone' || autoPhone),
  };
}

function statusOf(state, e) {
  const id = e.change?.id;
  const pend = state?.pending instanceof Map ? state.pending.get(id) : null;
  const phoneChecks = state?.phoneChecks instanceof Map ? state.phoneChecks : null;
  return checkStatus(e, {
    pendingWidth: pend?.width || null,
    phoneAuto: state?.phoneAuto || 'off',
    phoneCheck: phoneChecks ? phoneChecks.get(id) : undefined,
  });
}

// EDIT stage — why "Review & publish" is disabled (null = enabled). Needs at
// least one change and none blocked; measures still on their way are fine
// (the review shows them being checked).
//   state: { entries, uploading, uploads }
export function reviewDisabledReason(state) {
  const entries = state?.entries || [];
  if (entries.length === 0) return 'Make a change first.';
  if (entries.length > MAX_CHANGES) return `Publish at most ${MAX_CHANGES} changes at a time.`;
  if (state?.uploading) return 'Waiting for your picture to upload.';
  if (entries.some((e) => aggregateVerdict(e.measures) === 'blocked')) return 'Fix the changes that don’t fit first.';
  return imagesProblem(entries, state?.uploads);
}

// REVIEW stage — why "Publish to my website" is disabled (null = enabled):
// every change measured at both widths, none blocked, pictures described.
//   state: { entries, pending: Map id → { width }, phoneAuto, phoneChecks: Map,
//            publishing, uploading, uploads }
export function publishDisabledReason(state) {
  const entries = state?.entries || [];
  if (state?.publishing) return 'Publishing…';
  if (entries.length === 0) return 'There’s nothing to publish.';
  if (entries.length > MAX_CHANGES) return `Publish at most ${MAX_CHANGES} changes at a time.`;
  if (state?.uploading) return 'Waiting for your picture to upload.';
  const statuses = entries.map((e) => statusOf(state, e));
  if (statuses.some((s) => s.desktop === 'blocked' || s.phone === 'blocked')) return 'Fix or remove the changes that don’t fit first.';
  const images = imagesProblem(entries, state?.uploads);
  if (images) return images;
  if (statuses.some((s) => s.phone === 'checking')) return 'Checking your changes on phones…';
  if (statuses.some((s) => s.desktop === 'checking')) return 'Checking how your changes fit…';
  const phone = statuses.some((s) => s.phone === 'unchecked');
  const desktop = statuses.some((s) => s.desktop === 'unchecked');
  if (phone && desktop) return 'Check your changes on Desktop and Phone first.';
  if (phone) return 'Check your changes on Phone first.';
  if (desktop) return 'Check your changes on Desktop first.';
  return null;
}

// Changes on a page, for NEXSAV_INLINE_APPLY after navigation.
export function changesForPath(entries, path) {
  return (entries || []).map((e) => e.change || e).filter((c) => c.path === path);
}

// Normalise a page path the way the bridge reports it.
export function normalizePath(p) {
  let s = String(p || '/');
  if (s.length > 1) s = s.replace(/\/+$/, '');
  return s || '/';
}

// ---- Stage 2: publishing through the school repo's workflow, and undo ----
// (docs/inline-edit-lab.md §S2.3)

// A publish is under way: the workflow is running ('publishing') or its
// commit is being deployed ('deploying'). The panel shows one message for both.
export function isPublishingState(state) {
  return state === 'publishing' || state === 'deploying';
}

export const DAILY_PUBLISH_LIMIT = 20;
export const DAILY_LIMIT_MESSAGE = `You’ve published ${DAILY_PUBLISH_LIMIT} times today — that’s the daily limit. Try again tomorrow or send an edit request.`;
export const IN_FLIGHT_MESSAGE = 'Another publish for your site is still in progress. Wait for it to finish, then try again.';
export const TOO_MANY_CHANGES_MESSAGE = 'That’s too many changes to publish at once. Remove a few and publish them separately.';

function errorCode(err) {
  return err?.data?.code || err?.code || null;
}

// Plain words for a refused create / publish (429 too_many_publishes,
// 409 publish_in_flight); anything else keeps the API's own message.
export function publishErrorMessage(err, fallback = 'Could not save your changes.') {
  const code = errorCode(err);
  if (code === 'too_many_publishes') return DAILY_LIMIT_MESSAGE;
  if (code === 'publish_in_flight') return IN_FLIGHT_MESSAGE;
  if (code === 'payload_too_large') return TOO_MANY_CHANGES_MESSAGE;
  return (err && typeof err.message === 'string' && err.message) || fallback;
}

// Plain words for a refused undo.
export function revertErrorMessage(err) {
  const code = errorCode(err);
  if (code === 'not_latest') return 'A later publish on your site has to be undone first.';
  if (code === 'publish_in_flight') return IN_FLIGHT_MESSAGE;
  if (code === 'not_revertable') return 'This publish can’t be undone any more.';
  if (code === 'dispatch_failed') return 'We could not start undoing it. Try again in a few minutes.';
  return (err && typeof err.message === 'string' && err.message) || 'We could not undo this publish.';
}

// Stage 2 records a whole-publish failure (build, scan, timeout, …) on each
// change it kept off the site; a failure of one change is 'change_failed'.
// Mirrors WHOLE_PUBLISH_REASONS in functions/_shared/inline-edits.js.
const WHOLE_PUBLISH_REASONS = new Set([
  'no_build', 'no_report', 'no_commit', 'publisher_timeout', 'dispatch_failed', 'publisher_error',
  'asset_missing', 'asset_invalid',
  'bad_payload', 'tool_download', 'tool_integrity', 'asset_download', 'patch_error',
  'build_failed', 'scan_failed', 'commit_failed', 'nothing_to_commit', 'push_rejected',
  'cancelled', 'workflow_error',
]);
export function failureReasonFromChanges(changes) {
  const failed = (changes || []).filter((c) => c?.result?.status === 'failed');
  if (!failed.length) return null;
  const whole = failed.find((c) => WHOLE_PUBLISH_REASONS.has(c.result.reason));
  return whole ? whole.result.reason : 'change_failed';
}

const PUBLISH_STATE_LABELS = {
  publishing: 'Publishing',
  deploying: 'Deploying',
  live: 'Live',
  needs_review: 'Needs review',
  failed: 'Failed',
  deploy_failed: 'Deploy failed',
  reverting: 'Undoing',
  reverted: 'Reverted',
  revert_failed: 'Undo failed',
};
export function publishStateLabel(state) {
  return PUBLISH_STATE_LABELS[state] || 'Not published';
}

// Can this request's publish be undone from here (the server still checks
// that it is the site's latest publish)? A failed undo may be tried again.
export function canUndoPublish(er) {
  return !!er && er.source === 'inline' && !!er.mergeCommitSha
    && (er.publishState === 'live' || er.publishState === 'deploying' || er.publishState === 'revert_failed');
}

// ---- Stage 2b: the publishing flow in the panel, history and records ----
// (school-side trial feedback, approved 2026-09-27)

// Why a publish didn't happen, in plain words. The FAILED outcome already
// says "nothing on your website changed", so these don't repeat it.
const FAILURE_TEXT = {
  build_failed: 'Your website couldn’t be rebuilt with these changes.',
  scan_failed: 'The updated page didn’t pass our accessibility and layout check.',
  change_failed: 'We couldn’t place one of the changes safely.',
  publisher_unavailable: 'The publishing service didn’t answer in time.',
  asset_missing: 'One of the new pictures was missing. Choose it again, then try once more.',
  assets_too_large: 'The new pictures are too large to publish together. Try fewer or smaller pictures.',
  no_build: 'Publishing didn’t finish.',
  no_report: 'Publishing didn’t finish.',
  no_commit: 'Publishing didn’t finish.',
  publisher_error: 'Publishing didn’t finish.',
  publisher_timeout: 'Publishing took too long and was stopped. Try again in a few minutes.',
  dispatch_failed: 'We couldn’t start publishing. Try again in a few minutes.',
  deploy_failed: 'Your website didn’t finish updating. A NexSav team member has been told and will sort it out.',
  asset_invalid: 'One of the new pictures has a file name we can’t publish. Choose it again, then try once more.',
  bad_payload: 'Publishing couldn’t start.',
  tool_download: 'Our publishing tools couldn’t be loaded. Try again in a few minutes.',
  tool_integrity: 'Our publishing tools couldn’t be checked.',
  asset_download: 'One of the new pictures couldn’t be fetched. Try again in a few minutes.',
  patch_error: 'We couldn’t place these changes.',
  commit_failed: 'Publishing didn’t finish.',
  nothing_to_commit: 'Your website already shows these changes, so there was nothing to publish.',
  push_rejected: 'Your website was being updated at the same time. Try again.',
  cancelled: 'Publishing was stopped.',
  workflow_error: 'Publishing didn’t finish.',
  too_many_publishes: DAILY_LIMIT_MESSAGE,
  publish_in_flight: IN_FLIGHT_MESSAGE,
  payload_too_large: TOO_MANY_CHANGES_MESSAGE,
};
export function failureText(reason) {
  return (reason && FAILURE_TEXT[reason]) || null;
}
// Failures "Try again" can't fix from the panel.
export const NO_RETRY_REASONS = new Set(['deploy_failed', 'too_many_publishes', 'nothing_to_commit']);

// The workflow's own steps (publishProgress.step), in order.
export const PROGRESS_ORDER = ['received', 'patched', 'built', 'scanned', 'pushed', 'deploying'];

// What the person sees while a publish runs. `progress` = which workflow
// steps fill in each line's detail ("0 issues").
export const PUBLISH_STEPS = [
  { key: 'sent', label: 'Sent to NexSav', progress: ['received'] },
  { key: 'applied', label: 'Edits applied', progress: ['patched'] },
  { key: 'built', label: 'Website built', progress: ['built'] },
  { key: 'checked', label: 'Accessibility checked', progress: ['scanned'] },
  { key: 'publishing', label: 'Publishing to your website', progress: ['pushed', 'deploying'] },
  { key: 'live', label: 'Live' },
];

// A progress `detail` in words: a string as it is; a flat object by the keys
// we know ({ issues: 0 } → "0 issues", { applied, total }); anything else
// (a commit, timings) is not for school staff → null.
export function progressDetailText(detail) {
  if (typeof detail === 'string') return detail.trim() || null;
  if (!detail || typeof detail !== 'object') return null;
  if (typeof detail.text === 'string' && detail.text.trim()) return detail.text.trim();
  if (Number.isFinite(detail.issues)) return pluralize(detail.issues, 'issue');
  if (Number.isFinite(detail.applied) && Number.isFinite(detail.total)) {
    return `${detail.applied} of ${pluralize(detail.total, 'change')}`;
  }
  return null;
}

// The step list for a publish:
//   accepted     the portal accepted the publish (request created + publish started)
//   publishState the request's publishState
//   progress     its publishProgress ({ step, steps:[{ step, at, detail }] })
// → [{ key, label, state: 'done' | 'current' | 'todo', detail }]
// A later step implies the earlier ones (the lab runner reports none). Once
// the commit is pushed ('pushed' / 'deploying', or publishState 'deploying')
// "Publishing to your website" is the step under way — that is the site's
// own build — and it is ticked, with "Live", only when the request is live.
export function publishSteps({ accepted = false, publishState = null, progress = null } = {}) {
  const idx = progress && typeof progress.step === 'string' ? PROGRESS_ORDER.indexOf(progress.step) : -1;
  const live = publishState === 'live';
  const pushed = idx >= PROGRESS_ORDER.indexOf('pushed') || publishState === 'deploying' || live;
  const reached = [
    accepted || idx >= 0 || isPublishingState(publishState) || live,
    idx >= PROGRESS_ORDER.indexOf('patched'),
    idx >= PROGRESS_ORDER.indexOf('built'),
    idx >= PROGRESS_ORDER.indexOf('scanned') || pushed,
    live,
    live,
  ];
  for (let i = reached.length - 2; i >= 0; i -= 1) if (reached[i + 1]) reached[i] = true;
  const current = reached.indexOf(false);
  const details = new Map();
  const entries = Array.isArray(progress?.steps) ? progress.steps : (progress?.step ? [progress] : []);
  for (const e of entries) {
    const step = PUBLISH_STEPS.find((s) => s.progress?.includes(e?.step));
    const text = step ? progressDetailText(e.detail) : null;
    if (step && text) details.set(step.key, text);
  }
  return PUBLISH_STEPS.map((s, i) => ({
    key: s.key,
    label: s.label,
    state: reached[i] ? 'done' : (i === current ? 'current' : 'todo'),
    detail: details.get(s.key) || null,
  }));
}

// 83 s → "1:23".
export function formatElapsed(ms) {
  const total = Math.max(0, Math.floor((Number(ms) || 0) / 1000));
  const m = Math.floor(total / 60);
  const sec = total % 60;
  return `${m}:${String(sec).padStart(2, '0')}`;
}

// D1 timestamps are UTC without a zone ("2026-09-27 12:00:00").
export function parseServerDate(value) {
  if (!value) return null;
  if (value instanceof Date) return Number.isNaN(value.getTime()) ? null : value;
  const s = String(value);
  const iso = /[zZ]|[+-]\d\d:?\d\d$/.test(s) ? s.replace(' ', 'T') : `${s.replace(' ', 'T')}Z`;
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? null : d;
}

export function formatWhen(value, { time = true } = {}) {
  const d = parseServerDate(value);
  if (!d) return '';
  const opts = time
    ? { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' }
    : { month: 'short', day: 'numeric', year: 'numeric' };
  return d.toLocaleString('en-US', opts);
}

// The chip for an inline request ("website update") in lists.
const UPDATE_STATUS = {
  live: { label: 'Live', tone: 'success' },
  revert_failed: { label: 'Live', tone: 'success' },
  reverted: { label: 'Undone', tone: 'muted' },
  reverting: { label: 'Undoing…', tone: 'warning' },
  publishing: { label: 'Publishing…', tone: 'accent' },
  deploying: { label: 'Publishing…', tone: 'accent' },
  needs_review: { label: 'Under review', tone: 'warning' },
  failed: { label: 'Couldn’t publish', tone: 'danger' },
  deploy_failed: { label: 'Couldn’t publish', tone: 'danger' },
};
// A stopped publish the team finished by hand (Resolved) or declined (Closed).
const HANDLED_STATUS = {
  resolved: { label: 'Finished by hand', tone: 'success' },
  closed: { label: 'Closed', tone: 'muted' },
};
export function websiteUpdateStatus(er) {
  const key = er?.publishState || null;
  if (['needs_review', 'failed', 'deploy_failed'].includes(key) && HANDLED_STATUS[er?.status]) {
    return { key, ...HANDLED_STATUS[er.status] };
  }
  return { key, ...(UPDATE_STATUS[key] || { label: 'Not published', tone: 'muted' }) };
}

// How many changes on which pages: from the changes when the response has
// them, else its changeCount / pagePaths, else from the derived title ("Inline edits · 2 changes · /about +1
// more page").
export function summarizeWebsiteUpdate(er) {
  const changes = Array.isArray(er?.changes) ? er.changes : [];
  if (changes.length) {
    return { changeCount: changes.length, pages: [...new Set(changes.map((c) => c?.path).filter(Boolean))], morePages: 0 };
  }
  // The list's own fields (changeCount, pagePaths) when the API sends them.
  const paths = Array.isArray(er?.pagePaths) ? er.pagePaths.filter((p) => typeof p === 'string' && p) : [];
  if (Number.isFinite(er?.changeCount) || paths.length) {
    return {
      changeCount: Number.isFinite(er?.changeCount) ? er.changeCount : null,
      pages: paths.length ? paths : (er?.pagePath ? [er.pagePath] : []),
      morePages: 0,
    };
  }
  const m = /·\s*(\d+)\s+changes?\s*·\s*(\S+)(?:\s*\+(\d+)\s+more\s+pages?)?/.exec(String(er?.title || ''));
  if (m) return { changeCount: Number(m[1]), pages: [m[2]], morePages: Number(m[3] || 0) };
  const count = Number.isFinite(er?.changeCount) ? er.changeCount : null;
  return { changeCount: count, pages: er?.pagePath ? [er.pagePath] : [], morePages: 0 };
}

export function pagesText({ pages = [], morePages = 0 } = {}) {
  const base = pages.join(', ');
  return morePages > 0 ? `${base} and ${pluralize(morePages, 'more page')}` : base;
}

// A page of the school's site, without any editor parameters.
export function liveUrl(siteOrigin, path) {
  if (!siteOrigin) return null;
  try { return new URL(path || '/', siteOrigin).toString(); } catch { return null; }
}

// Per-change publish result → words.
const RESULT_LABEL = { applied: 'Applied', fallback: 'Needs a person', failed: 'Failed' };
export function resultLabel(status) {
  return RESULT_LABEL[status] || 'Not published yet';
}

// A two-line diff: the words both versions share at the start and the end,
// and the part in between that was removed / added.
export function textDiff(before, after) {
  const a = String(before ?? '').split(/(\s+)/).filter((x) => x !== '');
  const b = String(after ?? '').split(/(\s+)/).filter((x) => x !== '');
  let p = 0;
  while (p < a.length && p < b.length && a[p] === b[p]) p += 1;
  let s = 0;
  while (s < a.length - p && s < b.length - p && a[a.length - 1 - s] === b[b.length - 1 - s]) s += 1;
  return {
    prefix: a.slice(0, p).join(''),
    removed: a.slice(p, a.length - s).join(''),
    added: b.slice(p, b.length - s).join(''),
    suffix: a.slice(a.length - s).join(''),
  };
}

// The changes the page should now show (for NEXSAV_INLINE_VERIFY): what was
// applied (or has no result yet), without measures/results.
export function verifiableChanges(changes) {
  return (changes || [])
    .filter((c) => c && (!c.result || c.result.status === 'applied'))
    .map((c) => {
      const { measure, result, previewSrc, ...rest } = c; // eslint-disable-line no-unused-vars
      return rest;
    });
}

// After an undo the page should show the original words again: the text
// changes with before and after swapped (pictures can't be told apart by
// their address, so they aren't part of this check).
export function invertTextChanges(changes) {
  return verifiableChanges(changes)
    .filter((c) => c.kind === 'text')
    .map((c) => ({ ...c, before: { text: c.after?.text ?? '' }, after: { text: c.before?.text ?? '' } }));
}

// ---- Finishing a stuck website update from the admin record ----
// (GET / POST /api/admin/edit-requests/:id/finish)

// "app/families/parent-coordinator/page.tsx:47" → the file without its line.
// 'file:line', 'file:line:col' or 'file:line#n' (the nth copy on that line) → 'file'.
function withoutLine(fileLine) {
  return String(fileLine || '').replace(/:\d+(?::\d+)?(?:#\d+)?$/, '');
}

function titleCaseSegment(seg) {
  return String(seg || '')
    .replace(/^\[+\.{0,3}|\]+$/g, '') // [slug] / [...slug] → slug
    .split(/[-_\s]+/)
    .filter(Boolean)
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(' ');
}

// Source files whose name says little to a person.
const KNOWN_SOURCE_LABELS = {
  'navLinks.js': 'Menu and footer links',
  'navLinks.ts': 'Menu and footer links',
};

// A candidate place in the school's repository, in words:
//   app/page.tsx                              → "Home page"
//   app/families/parent-coordinator/page.tsx  → "Families › Parent Coordinator page"
//   components/Footer.jsx                     → "Footer component"
//   components/navLinks.js                    → "Menu and footer links (navLinks.js)"
//   anything else                             → the path itself
export function sourceLabel(fileLine) {
  const path = withoutLine(fileLine).replace(/^\.?\//, '').replace(/^src\//, '');
  const parts = path.split('/').filter(Boolean);
  const file = parts[parts.length - 1] || '';
  if (KNOWN_SOURCE_LABELS[file]) return `${KNOWN_SOURCE_LABELS[file]} (${file})`;
  if (parts[0] === 'app' && /^page\.(t|j)sx?$|^page\.mdx$/.test(file)) {
    const segs = parts.slice(1, -1)
      .filter((s) => !/^\(.*\)$/.test(s) && !s.startsWith('@')) // route groups, parallel slots
      .map(titleCaseSegment)
      .filter(Boolean);
    return segs.length ? `${segs.join(' › ')} page` : 'Home page';
  }
  if (parts[0] === 'components' && /\.(j|t)sx$/.test(file)) {
    return `${file.replace(/\.(j|t)sx$/, '')} component`;
  }
  return path || String(fileLine || '');
}

// The text of a change's before / after (`{ text }` or a plain string).
export function changeText(v) {
  if (typeof v === 'string') return v;
  return typeof v?.text === 'string' ? v.text : '';
}

// What the admin decides for one stuck change:
//   'pin'    ambiguous: pick one of the candidate places
//   'before' not_found: say what the site says now
//   'retry'  anything else: try it again as it is
export function finishMode(item) {
  const candidates = Array.isArray(item?.candidates) ? item.candidates : [];
  if (item?.reason === 'ambiguous' && candidates.length > 0) return 'pin';
  if (item?.reason === 'not_found' && item?.kind !== 'image') return 'before';
  return 'retry';
}

// The answers a stuck list starts with. not_found: the site's current
// wording (the server's suggestion, else the original before) and the new
// wording (the staff member's edit re-applied to it, else the original
// after); ambiguous: nothing picked.
export function initialFinishChoices(stuck) {
  const out = {};
  for (const item of stuck || []) {
    if (finishMode(item) === 'before') {
      out[item.changeId] = {
        beforeText: item.suggestedBefore ?? changeText(item.before),
        afterText: item.suggestedAfter ?? changeText(item.after),
      };
    }
  }
  return out;
}

export const FINISH_TEXT_MAX = 1000; // the publisher's own text limit (INLINE_LIMITS.maxText)
function textAnswerOk(v) {
  const t = String(v ?? '').trim();
  return t.length > 0 && t.length <= FINISH_TEXT_MAX;
}

function choiceComplete(item, choice) {
  const mode = finishMode(item);
  if (mode === 'pin') return !!choice?.pin && item.candidates.includes(choice.pin);
  if (mode === 'before') return String(choice?.beforeText ?? '').trim() !== '' && textAnswerOk(choice?.afterText ?? changeText(item.after));
  return true;
}

export function finishChoicesComplete(stuck, choices) {
  return (stuck || []).every((item) => choiceComplete(item, choices?.[item.changeId]));
}

// The POST body's decisions, in the stuck list's order.
export function buildFinishDecisions(stuck, choices) {
  return (stuck || []).map((item) => {
    const mode = finishMode(item);
    const choice = choices?.[item.changeId] || {};
    if (mode === 'pin') return { changeId: item.changeId, pin: choice.pin };
    if (mode === 'before') {
      const out = { changeId: item.changeId, beforeText: String(choice.beforeText ?? '').trim() };
      // The new wording goes along only when it isn't the original after.
      const after = String(choice.afterText ?? changeText(item.after)).trim();
      if (after !== changeText(item.after).trim()) out.afterText = after;
      return out;
    }
    return { changeId: item.changeId };
  });
}

const FINISH_RETRY_TEXT = {
  verify_failed: 'The live page didn’t show the change after publishing.',
  change_failed: 'This change couldn’t be placed safely.',
};

// Why a "try again" change stopped, in plain words.
export function finishRetryText(item) {
  return failureText(item?.reason) || FINISH_RETRY_TEXT[item?.reason] || (typeof item?.message === 'string' && item.message.trim())
    || 'This change didn’t go through.';
}

export const FINISH_IN_FLIGHT_TEXT = 'Another publish is running for this school. Try again when it finishes.';
const FINISH_BLOCKED_TEXT = {
  publish_in_flight: FINISH_IN_FLIGHT_TEXT,
  inline_edit_disabled: 'Website editing is switched off for this school, so this update can’t be finished from here.',
  publisher_unconfigured: 'Publishing isn’t set up for this school’s website, so this update can’t be finished from here.',
  not_stuck: 'Nothing in this update is waiting to be finished.',
  not_inline: 'Only website updates made in the editor can be finished here.',
};

// GET …/finish blockedReason → one calm line.
export function finishBlockedText(reason) {
  return FINISH_BLOCKED_TEXT[reason] || 'This update can’t be finished from here.';
}

// A refused POST …/finish, in plain words.
export function finishErrorMessage(err) {
  const code = errorCode(err);
  if (code === 'publish_in_flight') return FINISH_IN_FLIGHT_TEXT;
  if (code === 'not_stuck') return 'This update no longer needs finishing. Reopen it to see where it stands.';
  if (code === 'inline_edit_disabled') return FINISH_BLOCKED_TEXT.inline_edit_disabled;
  if (code === 'publisher_unconfigured' || err?.status === 503) return FINISH_BLOCKED_TEXT.publisher_unconfigured;
  if (err?.status === 0 || code === 'network_error') return 'We couldn’t reach the portal. Check your connection and try again.';
  return (err && typeof err.message === 'string' && err.message) || 'We couldn’t start publishing. Try again in a few minutes.';
}
